import { useMemo, useState } from 'react'
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import { useChat } from '../../context/useChat'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { Button } from '../Common'
import styles from './chat.module.css'

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

/**
 * Inline members card for the ChatPage 3C right sidecar. Non-modal variant of
 * `MemberManagerPanel` — same member list + add/remove/leave affordances,
 * rendered as a Design-Hub-style card the sidecar can host directly.
 */
export const ChannelMembersCard = () => {
  const { activeChannelId, channelsList, members, addMember, removeMember, leaveChannel } = useChat()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const { t } = useNls()
  const [busyUid, setBusyUid] = useState<string | null>(null)

  const channel = useMemo(
    () => channelsList.find((c) => c.id === activeChannelId) ?? null,
    [channelsList, activeChannelId],
  )
  const memberUids = useMemo(() => new Set(Object.keys(channel?.roles ?? {})), [channel])
  const isCurrentUserAdmin = currentUser ? channel?.roles[currentUser.uid] === 'admin' : false

  const presentMembers = useMemo(
    () => members.filter((m) => m.uid && memberUids.has(m.uid)),
    [members, memberUids],
  )
  const addableMembers = useMemo(
    () => (isCurrentUserAdmin ? members.filter((m) => m.uid && !memberUids.has(m.uid)) : []),
    [isCurrentUserAdmin, members, memberUids],
  )

  const handleRemove = async (uid: string) => {
    if (!channel) return
    setBusyUid(uid)
    try {
      await removeMember(channel.id, uid)
      showToast(t('NLSChatToastMemberRemoved'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSChatErrorRemoveMemberFailed'), 'error')
    } finally {
      setBusyUid(null)
    }
  }

  const handleAdd = async (uid: string) => {
    if (!channel) return
    setBusyUid(uid)
    try {
      await addMember(channel.id, uid)
      showToast(t('NLSChatToastMemberAdded'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSChatErrorAddMemberFailed'), 'error')
    } finally {
      setBusyUid(null)
    }
  }

  const handleLeave = async () => {
    if (!channel) return
    try {
      await leaveChannel(channel.id)
      showToast(t('NLSChatToastLeftChannel'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSChatErrorLeaveChannelFailed'), 'error')
    }
  }

  return (
    <section className="railPageShell__card--sidecar chatPage3C__sidecarCard chatPage3C__sidecarCard--members" aria-label={t('NLSChatLabelMembers')}>
      <div className="chatPage3C__sidecarCardHead">
        <h3 className={styles.modalTitle}>{t('NLSChatLabelMembers')}</h3>
        {channel && (
          <span className={styles.fieldLabel}>
            {t('NLSChatLabelInThisChannel', { count: presentMembers.length })}
          </span>
        )}
      </div>

      {!channel ? (
        <p className="chatPage3C__empty">{t('NLSChatEmptyNoChannelsCreateOne')}</p>
      ) : (
        <div className="chatPage3C__sidecarCardScroll">
          <div className={styles.memberPicker}>
            {presentMembers.map((member) => (
              <div key={member.uid} className={styles.memberRow}>
                <span className={styles.memberAvatar}>
                  {member.avatarUrl
                    ? <img src={member.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : initialsOf(member.name)}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {member.name}
                  {channel.roles[member.uid] === 'admin' && (
                    <span className={styles.channelLock} style={{ marginLeft: 6 }}> · {t('NLSChatLabelAdmin')}</span>
                  )}
                </span>
                {isCurrentUserAdmin && member.uid !== currentUser?.uid && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={<PersonRemoveOutlinedIcon />}
                    onClick={() => handleRemove(member.uid)}
                    loading={busyUid === member.uid}
                    aria-label={t('NLSChatActionRemoveMember')}
                    title={t('NLSChatActionRemoveMember')}
                  >
                    <span className="sr-only">{t('NLSChatActionRemove')}</span>
                  </Button>
                )}
              </div>
            ))}
          </div>

          {addableMembers.length > 0 && (
            <>
              <span className={styles.fieldLabel} style={{ marginTop: 8 }}>{t('NLSChatLabelAddMembers')}</span>
              <div className={styles.memberPicker} style={{ marginTop: 6 }}>
                {addableMembers.map((member) => (
                  <div key={member.uid} className={styles.memberRow}>
                    <span className={styles.memberAvatar}>
                      {member.avatarUrl
                        ? <img src={member.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : initialsOf(member.name)}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {member.name}
                    </span>
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon={<PersonAddOutlinedIcon />}
                      onClick={() => handleAdd(member.uid)}
                      loading={busyUid === member.uid}
                      aria-label={t('NLSChatActionAddMember')}
                      title={t('NLSChatActionAddMember')}
                    >
                      <span className="sr-only">{t('NLSChatActionAdd')}</span>
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {channel && (
        <div className="chatPage3C__sidecarCardFoot">
          <Button variant="tertiary" size="sm" icon={<LogoutIcon />} onClick={handleLeave}>
            {t('NLSChatActionLeaveChannel')}
          </Button>
        </div>
      )}
    </section>
  )
}
